// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// polyfills
import './utils/polyfills'; // includes Array.findIndex, Array.find, etc.

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

// Set up custom time locale (as in your original code)
d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
  shortMonths: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
});

// Global vars
let rawData;
let chartData;
let minDate;
let maxDate;
let minValue;
let maxValue;

const margins = {
  top: 30,
  right: 100,
  bottom: 0,
  left: 30,
};

// Initialize pym
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // In dev mode, populate meta text placeholders
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  // If embedded, track page views
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, window.pymChild.parentUrl.split('?')[0]);
  }

  // Fetch your multi-series data
  d3.json('https://ix.cnn.io/data/stock-index-trends/latest.json').then((json) => {
    rawData = json.data.map(d => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      index: d.index,
      change: +d.change_pct,
    })).filter(d => d.date < d3.timeParse('%Y-%m-%d')('2025-04-29'));

    // Compute overall extents
    minDate = d3.min(rawData, d => d.date);
    maxDate = d3.max(rawData, d => d.date);
    minValue = d3.min(rawData, d => d.change);
    maxValue = d3.max(rawData, d => d.change);

    // For multi-series, we just keep everything
    chartData = rawData;

    // Build dek and timestamp
    const dek = json.summary_sentence;
    const lastUpdated = json.last_updated_str;
    const lastClosing = json.latest_close_date;

    // Draw chart
    draw();
    window.onresize = draw;
  });
};

function draw() {
  // Container and sizing
  const container = d3.select('#graphic');
  container.html('');
  const width = container.node().clientWidth;
  const height = 400;
  const chartWidth = width - margins.left - margins.right;
  const chartHeight = height - margins.top - margins.bottom;

  // Mobile-friendly tick counts
  const isMobile = width <= 500;
  const xTicks = isMobile ? 3 : 4;
  const yTicks = isMobile ? 5 : 5;

  // Create scales
  const xScale = d3.scaleTime()
    .domain([minDate, maxDate])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0])
    .nice();

  // Axes
  const xAxis = d3.axisBottom(xScale)
    .ticks(xTicks)
    // Example: Show the year for the first tick or if month == 01
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) === '01') {
        return d3.timeFormat('%B %-d')(d);
      }
      return d3.timeFormat('%b %-d')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, ticks) => {
      const isTopTick = i === ticks.length - 1; // Check if it's the top tick
      if (isTopTick && d >= 1000) {
        return d3.format('.0%')(d);
      }
      return d3.format('.0%')(d);
    });

  // SVG
  const svg = container.append('svg')
    .attr('width', width)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  // g.append('g')
  //   .attr('class', 'grid x')
  //   .attr('transform', `translate(${[0, chartHeight]})`)
  //   .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const yGrid = d3.axisLeft(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat('');
  g.append('g')
    .attr('class', 'grid y')
    .call(yGrid);

  // Remove domain lines from the grids for a cleaner look
  g.selectAll('.grid.x .domain').remove();
  g.selectAll('.grid.y .domain').remove();

  g.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(0))
    .attr('y2', yScale(0))
    .attr('stroke', '#999')
    .attr('stroke-width', 1);

  // Multi-series line generator
  const line = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.change));

  // Group data by index using d3.nest (older d3)
  const series = d3.nest()
    .key(d => d.index)
    .entries(chartData);

  // Color scale
  const colorScale = d3.scaleOrdinal()
    .domain(series.map(s => s.key)) // e.g. ["S&P", "Dow", "Nasdaq"]
    .range(['#D58E25', '#D0A154', '#E7BD7D']);

  // Draw one line per index
  series.forEach((s) => {
    const indexName = s.key;
    const values = s.values.sort((a, b) => a.date - b.date);
    g.append('path')
      .datum(values)
      .attr('fill', 'none')
      .attr('stroke', colorScale(indexName))
      .attr('stroke-width', 2)
      .attr('d', line);

    // (Optional) final annotation circle + label for each line
    const lastPoint = values[values.length - 1];
    const lastX = xScale(lastPoint.date);
    const lastY = yScale(lastPoint.change);

    const anno = g.append('g')
      .attr('class', 'annotation')
      .attr('transform', `translate(${lastX},${lastY})`);

    anno.append('circle')
      .attr('r', 3)
      .attr('fill', colorScale(indexName));

    // offset the text a bit to avoid overlap
    const textElem = anno.append('text')
      .attr('dx', 6)
      .attr('dy', '0.3em')
      .style('font-size', '12px')
      .style('fill', '#262626')
      .attr('class', 'last-point');

    textElem.append('tspan')
      .style('font-weight', 'bold')
      .text(`${indexName}: `);

    textElem.append('tspan')
      .text(`${(lastPoint.change * 100).toFixed(1)}%`)
      .style('fill', '#8a8a8a');
  });

  d3.selectAll('text.last-point')
    .attr('dy', (d, i) => (i == 1 ? '-.8em' : '.3em'));

  // Helper function: finds the first data point for a given series on or after the annotation date
  function findNearestTradingData(annotationDate, seriesName) {
  // Filter chartData for points on or after annotationDate for the given series
    const filtered = chartData.filter(d => d.index === seriesName && d.date >= annotationDate);
    if (filtered.length > 0) {
      filtered.sort((a, b) => a.date - b.date);
      return filtered[0];
    }
    return null;
  }

  // Define your annotations array
  const annotations = [
    {
      date: '2025-02-03',
      index: 'Nasdaq', // attach annotation to the S&P line (default if not provided)
      offsetX: 0, // horizontal offset for the annotation text
      offsetY: 22, // vertical offset for the annotation text
      label: 'First trading',
      label2: 'after tariffs on',
      label3: 'China, Canada,',
      label4: 'Mexico delayed',
    },
    {
      date: '2025-02-14',
      index: 'Nasdaq', // attach annotation to the S&P line (default if not provided)
      offsetX: 0, // horizontal offset for the annotation text
      offsetY: 22, // vertical offset for the annotation text
      label: 'WH releases',
      label2: 'after tariffs on',
      label3: 'China, Canada,',
      label4: 'Mexico delayed',
    },
  ];

  // // Add annotations after drawing your main chart and legend
  // annotations.forEach((ann) => {
  //   const parseDate = d3.timeParse('%Y-%m-%d');
  //   const annDate = parseDate(ann.date);
  //   const seriesName = ann.index || 'S&P';

  //   // Find the nearest trading data point on or after the annotation date
  //   const dataPoint = findNearestTradingData(annDate, seriesName);
  //   if (!dataPoint) return; // skip if no trading data is found

  //   // Use the date from the found data point for x positioning
  //   const xPos = xScale(dataPoint.date);
  //   const yPos = yScale(dataPoint.change);

  //   // Draw a vertical dashed line from the point to the x-axis
  //   g.append('line')
  //     .attr('class', 'annotation-line')
  //     .attr('x1', xPos)
  //     .attr('x2', xPos)
  //     .attr('y1', yPos)
  //     .attr('y2', chartHeight) // x-axis position
  //     .attr('stroke', '#999')
  //     .attr('stroke-dasharray', '2,2');

  // // Create a group for the annotation text and apply offsets
  // const annGroup = g.append('g')
  //   .attr('class', 'annotation')
  //   .attr('transform', `translate(${xPos + (ann.offsetX || 0)}, ${yPos + (ann.offsetY || 0)})`);

  // // Append the date text (with the 'annotation-date' class)
  // annGroup.append('text')
  //   .attr('class', 'annotation-value')
  //   .attr('dx', '5px')
  //   .text(d3.timeFormat('%b %-d')(dataPoint.date));

  // // Append the label text (with the 'annotation-value' class)
  // annGroup.append('text')
  //   .attr('class', 'annotation-date')
  //   .attr('dx', '5px')
  //   .attr('dy', '14px')
  //   .text(ann.label);

  // annGroup.append('text')
  //   .attr('class', 'annotation-date')
  //   .attr('dx', '5px')
  //   .attr('dy', '28px')
  //   .text(ann.label2);

  // annGroup.append('text')
  //   .attr('class', 'annotation-date')
  //   .attr('dx', '5px')
  //   .attr('dy', '42px')
  //   .text(ann.label3);

  // annGroup.append('text')
  //   .attr('class', 'annotation-date')
  //   .attr('dx', '5px')
  //   .attr('dy', '56px')
  //   .text(ann.label4);
  // });

  // Let pym know the updated height
  window.pymChild.sendHeight();
}
